import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

import type { SuccessfulEnrichment } from 'hooks/useGruAgent/types';

import { useCanEditMessage } from 'hooks/useCanEditMessage';
import { useCapabilities } from 'hooks/useCapabilities/useCapabilities';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { getEnrichmentSuggestion, getPendingApprovalResultId } from 'hooks/useGruAgent/helpers';
import { useGruAgent } from 'hooks/useGruAgent/useGruAgent';
import { approveActionResult } from 'services/gru/gruApi';
import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import {
  GetMessageWithProcessSpecificationResponse,
  useMessageWithProcessSpecification,
} from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { getAcknowledgedAnnotationIds } from 'stores/annotationAcknowledgments/annotationAcknowledgments';
import { setDestinationProcess, setInitialCanonical, setProcessSpecificationFields } from 'stores/webedi/webedi';
import { getOriginalPDF } from 'support/helpers/messages/messages';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import {
  DocumentTypeWithVirtual,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  TransactionInternalType,
  TransactionsRoutes,
  UserDTO,
} from 'support/types';
import { Button } from 'components/Form/Button';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';
import { deriveDocumentDisplayConfig } from 'components/WebEDI/DocumentDisplayConfig/deriveDocumentDisplayConfig';
import { DocumentDisplayConfigProvider } from 'components/WebEDI/DocumentDisplayConfig/DocumentDisplayConfigContext';
import { applyJqProgram } from 'components/WebEDI/WebEDISidebar/AgentsTab/components/AgentSuggestionModal/applyJqProgram';
import { AGENT_WORKFLOWS_QUERY_KEY } from 'components/WebEDI/WebEDISidebar/hooks/useAgentsTabAvailability';

import { useEditMessage } from '../hooks/useEditMessage/useEditMessage';
import { useGruEnrichedPaths } from '../TransactionViewPage/MessageViewPage/hooks/useGruEnrichedPaths';
import { SendConfirmationModal } from '../components/CreateDocument/components/SendConfirmationModal/SendConfirmationModal';
import { EditMessageContent } from '../EditMessageContent/EditMessageContent';
import { revealPartyFields } from '../helpers';
import { UnknownTypePlaceholder } from '../TransactionViewPage/MessageViewPage/UnknownTypePlaceholder';
import { EditSuggestionsSidebar } from './components/EditSuggestionsSidebar/EditSuggestionsSidebar';

export const EditDocumentWithSuggestion = () => {
  useInitWebEDIStore();
  const { id } = useParams<{ id: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const { data: message, isError } = useMessageWithProcessSpecification({
    variables: {
      messageId: id,
    },
  });

  useCapabilities({ messageId: id });

  if (!id) {
    return null;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={id} />;
  }

  if (!currentUser || !currentPartner || !message) {
    return <Page isLoading>{null}</Page>;
  }

  return (
    <EditDocumentWithSuggestionContent
      id={id}
      message={message}
      currentUser={currentUser}
      currentPartner={currentPartner}
    />
  );
};

type EditDocumentWithSuggestionContentProps = {
  id: string;
  message: GetMessageWithProcessSpecificationResponse;
  currentUser: UserDTO;
  currentPartner: PartnerDTO;
};

const EditDocumentWithSuggestionContent = ({
  id,
  message,
  currentUser,
  currentPartner,
}: EditDocumentWithSuggestionContentProps) => {
  const navigate = useNavigate();
  const { data: messageData, fields: messageFields, origin_app: originApp } = message;
  const messageCanonical = messageData.canonical;

  const fields = useMemo(() => {
    if (!messageFields || !currentUser.isAdmin) {
      return messageFields;
    }
    return revealPartyFields(messageFields);
  }, [messageFields, currentUser.isAdmin]);

  const { data: originalFlattenData, uiConfig: originalUiConfig } = useFlattenDataAndUIConfig(fields, messageCanonical);

  useEffect(() => {
    setProcessSpecificationFields(fields);

    if (message?.data.canonical) {
      setInitialCanonical(message?.data.canonical);
    }

    if (message?.process) {
      setDestinationProcess(message?.process);
    }
  }, [message, fields]);

  const {
    workflowGroups,
    connected: agentIsConnected,
    agentName,
  } = useGruAgent({
    messageId: id,
    partnerId: currentPartner.id,
    partnerName: currentPartner.name,
    userName: currentUser.name,
    userLocale: currentUser.language,
    isAdmin: currentUser.isAdmin,
  });

  const latestGroup = workflowGroups[workflowGroups.length - 1];
  const workflow = latestGroup?.workflow ?? null;
  const actionResults = useMemo(() => latestGroup?.actionResults ?? [], [latestGroup?.actionResults]);

  const [pendingNavigation, setPendingNavigation] = useState(false);

  const handleEditSuccess = useCallback(async () => {
    if (!workflow) {
      return;
    }
    const resultId = getPendingApprovalResultId(actionResults);
    if (resultId) {
      try {
        await approveActionResult(agentName, resultId);
      } catch (error) {
        logError(error);
      }
    }
    setPendingNavigation(true);
  }, [agentName, workflow, actionResults]);

  const { editMessage, isUpdating, isSuccess, resetMessageQuery } = useEditMessage({
    message: messageData,
    processSpecificationFields: fields,
    isAdmin: currentUser.isAdmin,
    onSuccess: handleEditSuccess,
    skipQueryReset: true,
    withAIAgents: true,
  });

  useEffect(() => {
    if (pendingNavigation && workflow?.status === 'applied') {
      resetMessageQuery();
      queryClient.invalidateQueries({ queryKey: [AGENT_WORKFLOWS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: useGruEnrichedPaths.getKey() });
      navigate(routeToTransactionPage(TransactionInternalType.message, id));
    }
  }, [pendingNavigation, workflow?.status, navigate, id, resetMessageQuery]);

  const enrichmentSuggestion = getEnrichmentSuggestion(actionResults);
  const hasResults = actionResults.length > 0;
  const jqExpression = enrichmentSuggestion?.payload.jqExpression;

  const [revisedCanonical, setRevisedCanonical] = useState<Record<string, unknown> | undefined>();

  useEffect(() => {
    if (!messageCanonical || !jqExpression) {
      return;
    }

    let cancelled = false;
    applyJqProgram(messageCanonical, jqExpression).then((result) => {
      if (!cancelled) {
        setRevisedCanonical(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [messageCanonical, jqExpression]);

  const { data: revisedFlattenData } = useFlattenDataAndUIConfig(fields, revisedCanonical, 'revised');

  const originalPDF = useMemo(() => getOriginalPDF(originApp, messageData), [originApp, messageData]);

  // The 100ms timeout is a pragmatic guard against premature redirects.
  // When the workflow is done but no enrichment proposal was produced, we redirect
  // to the regular edit page. The short delay avoids racing against late-arriving
  // WebSocket messages that may still carry the proposal.
  useEffect(() => {
    const workflowDone =
      workflow && (workflow.status === 'completed' || workflow.status === 'applied' || workflow.status === 'failed');
    if (agentIsConnected && !enrichmentSuggestion && hasResults && workflowDone) {
      const timer = setTimeout(() => {
        navigate(routeToPage(TransactionsRoutes.edit, { id }), { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [agentIsConnected, enrichmentSuggestion, hasResults, workflow, navigate, id]);

  const isLoading = isUpdating || pendingNavigation;

  if (!revisedFlattenData || !originalFlattenData) {
    return <Page isLoading>{null}</Page>;
  }

  return (
    <EditDocumentWithSuggestionUI
      message={messageData}
      originalFlattenData={originalFlattenData}
      revisedFlattenData={revisedFlattenData}
      uiConfig={originalUiConfig}
      processSpecFields={fields}
      currentPartner={currentPartner}
      currentUser={currentUser}
      editMessage={editMessage}
      isLoading={isLoading}
      isSuccess={isSuccess}
      agentProposal={enrichmentSuggestion}
      canonical={messageCanonical}
      pdfUrl={originalPDF?.url}
      pdfFilename={originalPDF?.filename}
    />
  );
};

type EditDocumentWithSuggestionUIProps = {
  message: MessageV2DTO | undefined;
  originalFlattenData: Record<string, unknown>;
  revisedFlattenData: Record<string, unknown>;
  uiConfig: Array<import('@procuros/datachecks').ui.Section> | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  currentPartner: PartnerDTO | undefined;
  currentUser: UserDTO | undefined;
  editMessage: (formValues: Record<string, unknown>, annotationIds?: Array<string>) => void;
  isLoading: boolean;
  isSuccess: boolean;
  agentProposal: SuccessfulEnrichment | undefined;
  canonical: Record<string, unknown> | undefined | null;
  pdfUrl?: string;
  pdfFilename?: string;
};

const EditDocumentWithSuggestionUI = ({
  message,
  originalFlattenData,
  revisedFlattenData,
  uiConfig,
  processSpecFields,
  currentPartner,
  currentUser,
  editMessage,
  isLoading,
  isSuccess,
  agentProposal,
  canonical,
  pdfUrl,
  pdfFilename,
}: EditDocumentWithSuggestionUIProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<Record<string, unknown> | null>(null);

  const onSubmit = useCallback((formValues: Record<string, unknown>) => {
    const firstUnresolved = WebEDIErrorsServiceSingleton.getFirstUnresolvedAnnotation();
    if (firstUnresolved) {
      WebEDIErrorsServiceSingleton.setCurrentItem(firstUnresolved);
      return;
    }

    setPendingFormValues(formValues);
    setConfirmationModalOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmationModalOpen(false);
    if (pendingFormValues) {
      const ackIds = getAcknowledgedAnnotationIds();
      editMessage(pendingFormValues, ackIds);
    }
  }, [editMessage, pendingFormValues]);

  const handleCancelConfirmation = () => setConfirmationModalOpen(false);

  const handleCancel = useCallback(() => {
    navigate(routeToTransactionPage(TransactionInternalType.message, message?.id ?? ''));
  }, [navigate, message?.id]);

  const canEditMessage = useCanEditMessage(message, currentUser);
  const displayConfig = deriveDocumentDisplayConfig(message);

  return (
    <DocumentDisplayConfigProvider config={displayConfig}>
      <EditMessageContent
        message={message}
        data={revisedFlattenData}
        uiConfig={uiConfig}
        processSpecFields={processSpecFields}
        currentPartner={currentPartner}
        currentUser={currentUser}
        canEditMessage={canEditMessage}
        baseData={originalFlattenData}
        revisedData={revisedFlattenData}
        onSubmit={onSubmit}
        isSubmitting={isLoading}
        sidebar={
          agentProposal ? (
            <EditSuggestionsSidebar
              agentProposal={agentProposal}
              uiConfig={uiConfig}
              canonical={canonical}
              pdfUrl={pdfUrl}
              pdfFilename={pdfFilename}
            />
          ) : undefined
        }
        actionBar={
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
              analyticsId="webedi:agent_suggestion_cancel"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="webedi-document"
              RightIcon={PaperAirplaneIcon}
              loading={isLoading}
              analyticsId="webedi:agent_suggestion_submit"
            >
              {t('common:submit')}
            </Button>
          </div>
        }
        modals={
          <>
            <UnsavedChangesModal shouldBlock={!isSuccess} />
            <SendConfirmationModal
              onConfirm={handleConfirm}
              open={confirmationModalOpen}
              onCancel={handleCancelConfirmation}
              documentType={message?.type as DocumentTypeWithVirtual}
              receiverTradePartner={message?.receiver_partner ?? undefined}
            />
          </>
        }
      />
    </DocumentDisplayConfigProvider>
  );
};
